import React, { useRef, useEffect, useState } from 'react';
import CustomCheckbox from '../../../../components/common/CustomCheckbox';
import FloatingTooltip from '../../../../components/common/FloatingTooltip';
import { useNavigate } from '@tanstack/react-router';
import { useIntentSignal } from '../../../../hooks/useIntentSignal';
import dateIcon from '../../../../assets/date.svg';
import statusIcon from '../../../../assets/status.svg';
import engagementIcon from '../../../../assets/engage.svg';
import TableLoader from '../../../../components/common/TableLoader';
import empty from '../../../../assets/empty.svg';
import { toast } from 'react-toastify';
import { IoChevronDown } from "react-icons/io5";

export interface IntentSignal {
  _id: string;
  created_at: string;
  updated_at: string;
  signal: string;
  org_id: string;
  flow_type: string;
  keywords: string[];
  status: string;
  engagement_count: number;
  rulesets?: string[];
}

interface IntentSignalsTableProps {
  selectedSignals: IntentSignal[];
  setSelectedSignals: React.Dispatch<React.SetStateAction<IntentSignal[]>>;
  onSignalSelect: (signal: IntentSignal) => void;
  sortOption: string;
  intentSignals: IntentSignal[];
  totalCount: number;
  isFetchingSignals: boolean;
  fetchNextPage: () => Promise<any>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  error: Error | null;
  searchQuery?: string;
  updateIntentSignal?: any;
}

const HighlightedText: React.FC<{ text: string; searchQuery: string }> = ({ text, searchQuery }) => {
  if (!searchQuery.trim()) return <>{text}</>;

  const parts = text.split(new RegExp(`(${searchQuery})`, 'gi'));
  return (
    <>
      {parts.map((part, i) => (
        part.toLowerCase() === searchQuery.toLowerCase() ? (
          <span key={i} className="bg-[#F0AD4E] font-medium">{part}</span>
        ) : (
          <span key={i}>{part}</span>
        )
      ))}
    </>
  );
};

const StatusCell: React.FC<{ signal: IntentSignal }> = ({ signal }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { updateIntentSignal } = useIntentSignal();
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const statusOptions = [
    { value: 'active', label: 'Active', bgColor: 'bg-[#C8E6C9]', textColor: 'text-[#388E3C]' },
    { value: 'inactive', label: 'Inactive', bgColor: 'bg-[#F8BBD0]', textColor: 'text-[#C2185B]' },
    { value: 'running', label: 'Running', bgColor: 'bg-[#A0C4FF]', textColor: 'text-[#1B3B6F]' }
  ];

  const currentStatus = statusOptions.find(option => option.value === signal.status) || statusOptions[0];

  const handleStatusChange = async (newStatus: string) => {
    if (newStatus === signal.status || isUpdating) return;
    
    setIsUpdating(true);
    try {
      await updateIntentSignal.mutateAsync({
        id: signal._id,
        status: newStatus
      });
      
      toast.success(`Signal ${newStatus} successfully`, {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: "bg-[#FFFFFF] shadow-lg border border-[#EEEEEE] p-4 rounded-xl max-w-[440px] w-full",
        progressClassName: "bg-[#29DC5B]"
      });
    } catch (error) {
      console.error('Error updating signal status:', error);
      toast.error('Failed to update signal status');
    } finally {
      setIsUpdating(false);
      setIsOpen(false);
    }
  };

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={`
          inline-flex items-center gap-2 px-2.5 py-0.5 rounded-[8px] text-xs font-normal w-fit
          ${currentStatus.bgColor} ${currentStatus.textColor}
          ${isUpdating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
        disabled={isUpdating}
      >
        <span>{currentStatus.label}</span>
        <IoChevronDown className="w-3 h-3" />
      </button>

      {isOpen && (
        <div className="absolute flex flex-col left-0 mt-1 w-fit p-1 bg-[#FFFFFF] border border-[#EEEEEE] rounded-lg shadow-lg z-50">
          <div className="py-1">
            {statusOptions
              .filter(option => option.value !== signal.status)
              .map((option) => (
                <button
                  key={option.value}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStatusChange(option.value);
                  }}
                  className={`
                    w-full px-2 py-2 text-sm font-normal rounded-lg hover:bg-[#FAFAFA]
                    flex items-center gap-2
                  `}
                >
                  <span className={`${option.textColor}`}>
                    <span className={`${option.textColor} text-[20px]`}>•&nbsp;</span>
                    <span className={`${option.textColor}`}>{option.label}</span>
                  </span>
                </button>            
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

const IntentSignalsTable: React.FC<IntentSignalsTableProps> = ({ 
  selectedSignals, 
  setSelectedSignals,
  onSignalSelect,
  sortOption,
  intentSignals,
  totalCount,
  isFetchingSignals,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  error,
  searchQuery
}) => {
  const navigate = useNavigate();
  const [isNavigating, setIsNavigating] = useState(false);
  const [allFetchedSignals, setAllFetchedSignals] = useState<IntentSignal[]>([]);
  const itemsPerPage = 20;

  const tableBodyRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.5 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const [hoveredSignal, setHoveredSignal] = useState<string | null>(null);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedSignals(allFetchedSignals);
    } else {
      setSelectedSignals([]);
    }
  };

  const handleSelectSignal = (index: number, checked: boolean) => {
    if (checked) {
      setSelectedSignals([...selectedSignals, allFetchedSignals[index]]);
    } else {
      setSelectedSignals(selectedSignals.filter(signal => signal._id !== allFetchedSignals[index]._id));
    }
  };

  const handleSignalClick = (signal: IntentSignal) => {
    window.history.pushState({}, '', `/intent-signals/${signal._id}`);
    onSignalSelect(signal);
  };

  const handleEngagementClick = (signal: IntentSignal, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate({
      to: '/accounts',
      search: { intent_signal_id: signal._id }
    });
  };

  const columns = [
    { width: '36%', minWidth: '300px' },
    { width: '16%', minWidth: '150px' },
    { width: '16%', minWidth: '150px' },
    { width: '16%', minWidth: '150px' },
    { width: '16%', minWidth: '150px' }
  ];

  useEffect(() => {
    setAllFetchedSignals([]);
  }, [sortOption]);

  useEffect(() => {
    if (intentSignals.length > 0) {
      setAllFetchedSignals(intentSignals);
    }
  }, [intentSignals]);

  return (
    <div className="intent-signals-table w-full flex items-center justify-center">
      <div className="flex flex-col w-full h-[calc(100vh-180px)]">
        <div className="flex-1 w-full overflow-hidden flex flex-col">

          <div className="w-full border border-[#EEEEEE] flex flex-col h-full">
            {isFetchingSignals ? (
              <div className="h-full">
                <TableLoader columns={columns} />
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center h-full">
                <div className="flex flex-col items-center max-w-[440px] text-center bg-white p-8 rounded-2xl shadow-sm border border-[#EEEEEE]">
                  <div className="w-16 h-16 mb-6 rounded-full bg-[#FEE2E2] flex items-center justify-center">
                    <svg 
                      className="w-8 h-8 text-[#E02C16]" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
                      />
                    </svg>
                  </div>

                  <h3 className="text-[20px] font-semibold text-[#222222] mb-3">
                    Error loading intent signals
                  </h3>
                  
                  <p className="text-[15px] text-[#666666] mb-8 leading-relaxed">
                    {error instanceof Error ? error.message : String(error)}
                  </p>

                  <div className="flex gap-4">
                    <button 
                      onClick={() => window.location.reload()}
                      className="inline-flex items-center px-5 py-2.5 bg-[#E02C16] text-white text-sm font-normal rounded-lg hover:bg-[#c72512] transition-colors gap-2"
                    >
                      <svg 
                        className="w-4 h-4" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                        />
                      </svg>
                      Retry
                    </button>
                  </div>
                </div>
              </div>
            ) : intentSignals.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full border border-[#EEEEEE] rounded-lg m-4">
                   <div className="flex flex-col items-center max-w-[400px] text-center">
                      <img src={empty} alt="Empty" className="w-[160px] h-[85px] mb-2" />
                      <div className="flex flex-col items-center justify-center mt-6">
                        <h3 className="text-[16px] font-semibold text-[#000000] mb-3">
                          No intent signals have been created yet.
                        </h3>
                        <p className="text-[14px] text-[#7A7A7A] font-normal mb-3">
                          Start creating now!
                        </p>
                      </div>
                    </div>
                </div>
            ) : (
              <>
                <table className="w-full table-fixed">
                  <thead>
                    <tr className="border-b bg-[#FFFFFF] z-10">
                      <th className="pl-6 text-right w-[40px] min-w-[40px] h-[40px]">
                        <CustomCheckbox
                          checked={!isFetchingSignals && selectedSignals.length === allFetchedSignals.length && allFetchedSignals.length > 0}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th className="px-4 text-left  text-sm font-normal text-[#222222] w-[36%] min-w-[300px] border-r border-[#EEEEEE] h-[40px]">
                        Intent Signal
                      </th>
                      <th className="px-4 text-left  text-sm font-normal text-[#222222] w-[16%] min-w-[150px] border-r border-[#EEEEEE] h-[40px]">
                        <div className="flex items-center gap-[6px]">
                          <img src={dateIcon} alt="Date" className="w-[14px] h-[14px]" />
                          Created on
                        </div>
                      </th>
                      <th className="px-4 text-left  text-sm font-normal text-[#222222] w-[16%] min-w-[150px] border-r border-[#EEEEEE] h-[40px]">
                        <div className="flex items-center gap-[6px]">
                          <img src={dateIcon} alt="Date" className="w-[14px] h-[14px]" />
                          Last Run
                        </div>
                      </th>
                      <th className="px-4 text-left  text-sm font-normal text-[#222222] w-[16%] min-w-[150px] border-r border-[#EEEEEE] h-[40px]">
                        <div className="flex items-center gap-[6px]">
                          <img src={statusIcon} alt="Status" className="w-[14px] h-[14px]" />
                          Status
                        </div>
                      </th>
                      <th className="px-4 text-left  text-sm font-normal text-[#222222] w-[16%] min-w-[150px] border-r border-[#EEEEEE] h-[40px]">
                        <div className="flex items-center gap-[6px]">
                          <img src={engagementIcon} alt="Engagement" className="w-[14px] h-[14px]" />
                          Lead Count
                        </div>
                      </th>
                    </tr>
                  </thead>
                </table>
                <div 
                  ref={tableBodyRef}
                  className="flex-1 overflow-y-auto scrollbar-hide min-h-0"
                  style={{ 
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none'
                  }}
                >
                  <table className="w-full table-fixed">
                    <tbody className="bg-[#FFFFFF]">
                      {allFetchedSignals.map((signal, index) => (
                        <tr 
                          key={signal._id}
                          className="border-b border-[#EEEEEE] hover:bg-[#FFFFFF] cursor-pointer"
                          onClick={() => handleSignalClick(signal)}
                        >
                          <td 
                            className="pl-6 pt-2 text-right w-[40px] min-w-[40px] h-[36px]"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <CustomCheckbox
                              checked={selectedSignals.some(s => s._id === intentSignals[index]._id)}
                              onChange={(checked) => handleSelectSignal(index, checked)}
                            />
                          </td>
                          <td 
                            className="px-4 text-left text-[14px] font-normal text-[#222222] w-[36%] min-w-[300px] border-r border-[#EEEEEE] h-[36px] relative"
                          >
                            <div 
                              className="truncate"
                              onMouseEnter={() => setHoveredSignal(signal.signal)}
                              onMouseLeave={() => setHoveredSignal(null)}
                            >
                              <HighlightedText text={signal.signal} searchQuery={searchQuery || ''} />
                            </div>
                            {hoveredSignal === signal.signal && (
                              <FloatingTooltip 
                                text={signal.signal} 
                                isVisible={true}
                              />
                            )}
                          </td>
                          <td className="px-4 text-left text-[14px] font-light text-[#7A7A7A] w-[16%] min-w-[150px] border-r border-[#EEEEEE] h-[36px]">
                            {new Date(signal.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </td>
                          <td className="px-4 text-left text-[14px] font-light text-[#7A7A7A] w-[16%] min-w-[150px] border-r border-[#EEEEEE] h-[36px]">
                            {new Date(signal.updated_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </td>
                          <td 
                            className="px-4 text-left text-[14px] font-light text-[#222222] w-[16%] min-w-[150px] border-r border-[#EEEEEE] h-[36px]"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <StatusCell signal={signal} />
                          </td>
                          <td 
                            className="px-4 text-left text-[14px] font-light text-[#266DF0] w-[16%] min-w-[150px] border-r border-[#EEEEEE] h-[36px] underline cursor-pointer"
                            onClick={(e) => handleEngagementClick(signal, e)}
                          >
                            {signal.engagement_count}
                          </td>
                        </tr>
                      ))}
                      {isFetchingNextPage && (
                        <>
                          <tr className="animate-pulse">
                            <td className="pl-6 w-[40px]">
                              <div className="h-4 w-4 bg-[#EEEEEE] rounded"></div>
                            </td>
                            {columns.map((column, idx) => (
                              <td 
                                key={idx} 
                                className="px-4 border-r border-[#EEEEEE] h-[36px]"
                                style={{ 
                                  width: column.width,
                                  minWidth: column.minWidth
                                }}
                              >
                                <div className="h-4 bg-[#EEEEEE] rounded w-3/4"></div>
                              </td>
                            ))}
                          </tr>
                          <tr className="animate-pulse">
                            <td className="pl-6 w-[40px]">
                              <div className="h-4 w-4 bg-[#EEEEEE] rounded"></div>
                            </td>
                            {columns.map((column, idx) => (
                              <td 
                                key={idx} 
                                className="px-4 border-r border-[#EEEEEE] h-[36px]"
                                style={{ 
                                  width: column.width,
                                  minWidth: column.minWidth
                                }}
                              >
                                <div className="h-4 bg-[#EEEEEE] rounded w-3/4"></div>
                              </td>
                            ))}
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>
                  <div ref={loadMoreRef} className="w-full h-20">
                    {isFetchingNextPage && (
                      <div className="flex justify-center py-4">
                        <div className="animate-pulse w-20 h-4 bg-[#EEEEEE] rounded"></div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntentSignalsTable; 